import { ref } from 'vue'
import { getNotificationList } from '@/api/modules/notification'

export const NOTIFICATION_PAGE_SIZE = 20

/** 管理服务端分页；scope 校验身份，isOpen 校验抽屉生命周期，supported 校验后端能力。 */
class NotificationListState {
  readonly notifications = ref<AppNotification[]>([])
  readonly group = ref<NotificationGroup>('message')
  readonly category = ref<NotificationCategory>('all')
  readonly unreadOnly = ref(false)
  readonly notificationPage = ref(1)
  readonly notificationTotal = ref(0)
  readonly listReady = ref(false)
  readonly loading = ref(false)
  readonly listError = ref('')
  private sequence = 0
  /** 保存状态依赖；不触发接口请求。 */
  constructor(
    private readonly scope: () => string,
    private readonly isOpen: () => boolean,
    private readonly supported: () => boolean | null,
  ) {}

  /** 关闭或切换上下文时使未完成的列表查询失效。 */
  invalidate = () => {
    this.sequence++
    this.loading.value = false
  }

  /** 清除旧结果，不让新筛选下显示旧分类数据。 */
  clearResults = () => {
    this.invalidate()
    this.notifications.value = []
    this.notificationTotal.value = 0
    this.listReady.value = false
    this.listError.value = ''
  }

  /** 重开抽屉或更换身份时回到默认筛选。 */
  reset = () => {
    this.clearResults()
    this.group.value = 'message'
    this.category.value = 'all'
    this.unreadOnly.value = false
    this.notificationPage.value = 1
  }

  /** 使用 filters 更新服务端筛选，一级变化重置二级但保留未读条件。 */
  setFilters = (
    filters: Partial<Pick<NotificationListParams, 'group' | 'category' | 'unreadOnly'>>,
  ) => {
    this.clearResults()
    if (filters.group !== undefined) {
      this.group.value = filters.group
      this.category.value = 'all'
    }
    if (filters.category !== undefined) this.category.value = filters.category
    if (filters.unreadOnly !== undefined) this.unreadOnly.value = filters.unreadOnly
    this.notificationPage.value = 1
    return this.fetchNotifications()
  }

  /** 读取当前筛选页；总数缩小时最多重定位两次，防止持续并发删除导致无限重试。 */
  fetchNotifications = async () => {
    if (!this.scope() || !this.isOpen() || this.supported() !== true) return false
    const currentScope = this.scope()
    const request = ++this.sequence
    const params = {
      group: this.group.value,
      category: this.category.value,
      unreadOnly: this.unreadOnly.value,
      page: this.notificationPage.value,
      pageSize: NOTIFICATION_PAGE_SIZE,
    }
    this.loading.value = true
    this.listError.value = ''
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await getNotificationList(params)
        if (request !== this.sequence || currentScope !== this.scope() || !this.isOpen())
          return false
        const lastPage = Math.max(1, Math.ceil(result.total / NOTIFICATION_PAGE_SIZE))
        if (params.page > lastPage) {
          params.page = lastPage
          continue
        }
        this.notifications.value = result.list || []
        this.notificationTotal.value = result.total
        this.notificationPage.value = params.page
        this.listReady.value = true
        return true
      }
      throw new Error('消息分页变化，请重试')
    } catch {
      if (request === this.sequence && currentScope === this.scope() && this.isOpen())
        this.listError.value = '消息加载失败，请重试'
      return false
    } finally {
      if (request === this.sequence && currentScope === this.scope()) this.loading.value = false
    }
  }

  /** 切换 page 页；清掉其他页数据，失败时不冒充本页结果。 */
  setPage = (page: number) => {
    this.invalidate()
    this.notifications.value = []
    this.listReady.value = false
    this.notificationPage.value = page
    return this.fetchNotifications()
  }
}

/** 创建隔离的通知状态；参数分别提供当前身份及相关数据状态。 */
export function useNotificationList(
  scope: () => string,
  isOpen: () => boolean,
  supported: () => boolean | null,
) {
  const state = new NotificationListState(scope, isOpen, supported)
  return {
    notifications: state.notifications,
    group: state.group,
    category: state.category,
    unreadOnly: state.unreadOnly,
    notificationPage: state.notificationPage,
    notificationTotal: state.notificationTotal,
    listReady: state.listReady,
    loading: state.loading,
    listError: state.listError,
    reset: state.reset,
    invalidate: state.invalidate,
    setFilters: state.setFilters,
    setPage: state.setPage,
    fetchNotifications: state.fetchNotifications,
  }
}
